import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Table,
  Button,
  Badge,
  Spinner,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Input,
  InputGroup,
  InputGroupText,
} from 'reactstrap';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import axios from 'axios';
import { BASE_URL } from '../constants/apiConfig';
import './ReviewerDashboard.css';

const ReviewerDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ 'Round 1': {}, 'Round 2': {} });
  const [applications, setApplications] = useState([]);
  const [filteredApplications, setFilteredApplications] = useState([]);
  const [activeTab, setActiveTab] = useState('round1');
  const [filter, setFilter] = useState('all'); // all, pending, completed
  const [searchTerm, setSearchTerm] = useState('');
  const [userReviewRound, setUserReviewRound] = useState('Both');

  useEffect(() => {
    fetchStats();
    fetchApplications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, filter]);

  // Set initial active tab based on user's review round
  useEffect(() => {
    if (userReviewRound === 'Round 1') {
      setActiveTab('round1');
    } else if (userReviewRound === 'Round 2') {
      setActiveTab('round2');
    }
  }, [userReviewRound]);

  const fetchStats = async () => {
    try {
      const token = sessionStorage.getItem('token');
      const response = await axios.get(`${BASE_URL}/api/reviewer/dashboard/stats`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = response.data.data;
      
      // Extract userReviewRound from response
      const { userReviewRound: reviewRound, ...statsData } = data;
      setStats(statsData);
      setUserReviewRound(reviewRound || 'Both');
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchApplications = async () => {
    try {
      const round = {
        round1: 'Round 1',
        round2: 'Round 2',
      };
      setLoading(true);
      const token = sessionStorage.getItem('token');
      const params = { round: round[activeTab] || 'Round 1' };
      if (filter !== 'all') {
        params.status = filter;
      }

      const response = await axios.get(`${BASE_URL}/api/reviewer/applications`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });

      const apps = response.data.data.applications || [];
      setApplications(apps);
      setFilteredApplications(apps);
    } catch (error) {
      console.error('Error fetching applications:', error);
      toast.error('Failed to load applications');
    } finally {
      setLoading(false);
    }
  };

  const handleReviewClick = (applicationId) => {
    navigate(`/reviewer/review/${applicationId}`);
  };

  const handleSearch = (value) => {
    setSearchTerm(value);
    const term = value.toLowerCase().trim();

    if (!term) {
      setFilteredApplications(applications);
      return;
    }

    const filtered = applications.filter(app => {
      const id = app.id?.toString() || '';
      const name = app.fullName?.toLowerCase() || '';
      const program = app.program?.toLowerCase() || '';
      
      return id.includes(term) || name.includes(term) || program.includes(term);
    });

    setFilteredApplications(filtered);
  };

  const getStatusBadge = (status) => {
    const colors = {
      Pending: 'warning',
      Completed: 'success',
      'In-Progress': 'info',
    };
    return <Badge color={colors[status] || 'secondary'}>{status}</Badge>;
  };

  const renderStatsCard = (title, stats = {}, color) => {
    return (
      <Card className="h-100">
        <CardBody>
          <h6 className="text-muted mb-3">{title}</h6>
          <Row className="text-center">
            <Col>
              <h3 className={`text-${color} mb-0`}>{stats?.total || 0}</h3>
              <small className="text-muted">Total</small>
            </Col>
            <Col>
              <h3 className="text-warning mb-0">{stats?.pending || 0}</h3>
              <small className="text-muted">Pending</small>
            </Col>
            <Col>
              <h3 className="text-success mb-0">{stats?.completed || 0}</h3>
              <small className="text-muted">Completed</small>
            </Col>
          </Row>
        </CardBody>
      </Card>
    );
  };

  return (
    <div className="reviewer-dashboard">
      <Container>
        {/* Header */}
        <Row className="mb-4">
          <Col>
            <h2>Reviewer Dashboard</h2>
            <p className="text-muted">Review assigned fellowship applications</p>
          </Col>
        </Row>

        {/* Statistics Cards */}
        <Row className="mb-4">
          {(userReviewRound === 'Round 1' || userReviewRound === 'Both') && (
            <Col md={userReviewRound === 'Both' ? 6 : 12}>
              {renderStatsCard('Round 1 Reviews', stats['Round 1'], 'primary')}
            </Col>
          )}
          {(userReviewRound === 'Round 2' || userReviewRound === 'Both') && (
            <Col md={userReviewRound === 'Both' ? 6 : 12}>
              {renderStatsCard('Round 2 Reviews', stats['Round 2'], 'info')}
            </Col>
          )}
        </Row>

        {/* Applications List */}
        <Card>
          <CardHeader className="bg-light">
            <Row className="align-items-center">
              <Col>
                <h5 className="mb-0">Assigned Applications</h5>
              </Col>
              <Col xs="auto">
                <div className="btn-group btn-group-sm">
                  <Button
                    color={filter === 'all' ? 'primary' : 'outline-primary'}
                    onClick={() => setFilter('all')}
                  >
                    All
                  </Button>
                  <Button
                    color={filter === 'pending' ? 'warning' : 'outline-warning'}
                    onClick={() => setFilter('pending')}
                  >
                    Pending
                  </Button>
                  <Button
                    color={filter === 'completed' ? 'success' : 'outline-success'}
                    onClick={() => setFilter('completed')}
                  >
                    Completed
                  </Button>
                </div>
              </Col>
            </Row>
          </CardHeader>
          <CardBody>
            {/* Search Bar */}
            <Row className="mb-3">
              <Col md={6}>
                <InputGroup>
                  <InputGroupText>
                    <i className="bi bi-search"></i>
                  </InputGroupText>
                  <Input
                    type="text"
                    placeholder="Search by Name, Program, or Application ID..."
                    value={searchTerm}
                    onChange={(e) => handleSearch(e.target.value)}
                  />
                  {searchTerm && (
                    <Button
                      color="secondary"
                      outline
                      onClick={() => handleSearch('')}
                    >
                      <i className="bi bi-x"></i>
                    </Button>
                  )}
                </InputGroup>
              </Col>
              <Col md={6} className="text-end">
                <small className="text-muted">
                  Showing {filteredApplications.length} of {applications.length} application(s)
                </small>
              </Col>
            </Row>

            {/* Round Tabs */}
            <Nav tabs className="mb-3">
              {(userReviewRound === 'Round 1' || userReviewRound === 'Both') && (
                <NavItem>
                  <NavLink
                    className={activeTab === 'round1' ? 'active' : ''}
                    onClick={() => setActiveTab('round1')}
                    style={{ cursor: 'pointer' }}
                  >
                    Round 1 ({stats['Round 1']?.total || 0})
                  </NavLink>
                </NavItem>
              )}
              {(userReviewRound === 'Round 2' || userReviewRound === 'Both') && (
                <NavItem>
                  <NavLink
                    className={activeTab === 'round2' ? 'active' : ''}
                    onClick={() => setActiveTab('round2')}
                    style={{ cursor: 'pointer' }}
                  >
                    Round 2 ({stats['Round 2']?.total || 0})
                  </NavLink>
                </NavItem>
              )}
            </Nav>

            <TabContent activeTab={activeTab}>
              <TabPane tabId={activeTab}>
                {loading ? (
                  <div className="loading-spinner" style={{ minHeight: '50vh' }}>
                    <Spinner color="primary" style={{ width: '3rem', height: '3rem' }} />
                    <p className="mt-3">Loading applications...</p>
                  </div>
                ) : filteredApplications.length === 0 ? (
                  <div className="text-center py-5">
                    <i className="bi bi-inbox display-4 text-muted"></i>
                    <p className="mt-3 text-muted">
                      {searchTerm ? 'No applications found matching your search' : 'No applications assigned'}
                    </p>
                  </div>
                ) : (
                  <Table hover responsive>
                    <thead>
                      <tr>
                        <th>Application ID</th>
                        <th>Applicant Name</th>
                        <th>Email</th>
                        <th>Program</th>
                        <th>Themes</th>
                        <th>Submitted Date</th>
                        <th>Review Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredApplications.map((app) => (
                        <tr key={app.id}>
                          <td>
                            <code>#{app.id}</code>
                          </td>
                          <td className="fw-bold">{app.fullName}</td>
                          <td>{app.email}</td>
                          <td>
                            <small>{app.program || 'N/A'}</small>
                          </td>
                          <td>
                            <small>
                              {Array.isArray(app.themes)
                                ? app.themes.slice(0, 2).join(', ') +
                                  (app.themes.length > 2 ? '...' : '')
                                : 'N/A'}
                            </small>
                          </td>
                          <td>
                            {app.submitted_at
                              ? format(new Date(app.submitted_at), 'MMM dd, yyyy')
                              : 'N/A'}
                          </td>
                          <td>{getStatusBadge(app.review_status)}</td>
                          <td>
                            <Button
                              color={app.review_status === 'Completed' ? 'info' : 'primary'}
                              size="sm"
                              onClick={() => handleReviewClick(app.id)}
                            >
                              {app.review_status === 'Completed' ? (
                                <>
                                  <i className="bi bi-eye"></i> View
                                </>
                              ) : (
                                <>
                                  <i className="bi bi-pencil"></i> Review
                                </>
                              )}
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </TabPane>
            </TabContent>
          </CardBody>
        </Card>
      </Container>
    </div>
  );
};

export default ReviewerDashboard;
